import { io, Socket } from "socket.io-client";
import { getAuthToken } from "./authToken";

let socket: Socket | null = null;
let isInitializing = false;
let eventListenersAttached = false;

export function getSocket(): Socket | null {
  return socket;
}

export function initializeSocket(): Socket | null {
  // Require token so server accepts connection and adds client to tenant room
  const token = getAuthToken();
  if (!token) {
    if (socket && socket.disconnected) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
      eventListenersAttached = false;
    }
    isInitializing = false;
    return null;
  }

  // If socket exists and is connected, return it immediately
  if (socket?.connected) {
    return socket;
  }

  // If initialization is already in progress, return existing socket (even if not connected)
  // This prevents race conditions when multiple components call useSocket() simultaneously
  if (isInitializing && socket) {
    return socket;
  }

  // If socket exists but is disconnected, clean it up first
  if (socket && socket.disconnected) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    eventListenersAttached = false;
  }

  isInitializing = true;

  // Determine the correct server URL
  // In development: backend runs on port 5000, frontend on 5173
  // In production: use VITE_API_URL or same origin
  let serverUrl: string;
  
  // Check if VITE_API_URL is set and valid
  const viteApiUrl = import.meta.env.VITE_API_URL;
  if (viteApiUrl && typeof viteApiUrl === 'string' && viteApiUrl.trim() !== '') {
    serverUrl = viteApiUrl.trim();
    // Ensure it's a valid URL with protocol
    if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
      // If no protocol, assume https for production
      serverUrl = `https://${serverUrl}`;
    }
    // Validate the URL
    try {
      const url = new URL(serverUrl);
      // Ensure it has a valid hostname and port (if needed)
      if (!url.hostname || url.hostname === 'undefined') {
        throw new Error('Invalid hostname');
      }
    } catch (error) {
      serverUrl = import.meta.env.DEV ? "http://localhost:5000" : window.location.origin;
    }
  } else if (import.meta.env.DEV) {
    // Development mode - connect to backend on port 5000
    serverUrl = "http://localhost:5000";
  } else {
    // Production - use same origin
    serverUrl = window.location.origin;
  }

  // Final validation - ensure URL is valid before passing to Socket.IO
  try {
    const testUrl = new URL(serverUrl);
    if (!testUrl.hostname || testUrl.hostname === 'undefined') {
      throw new Error('Invalid hostname in URL');
    }
  } catch (error) {
    console.error("[Socket] Invalid server URL after validation:", serverUrl, error);
    // Fallback to same origin as last resort
    serverUrl = window.location.origin;
  }
  
  
  socket = io(serverUrl, {
    path: "/socket.io/", // Explicitly set Socket.IO path
    transports: ["polling", "websocket"], // Try polling first (works better with proxies)
    autoConnect: true,
    // Add timeout and retry configuration
    timeout: 10000,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 3,
    // Force new connection to avoid stale connections
    forceNew: false,
    // Send JWT token in auth (required for server to add client to tenant room)
    auth: { token },
    // Also send as query parameter as fallback
    query: { token },
  });

  // Only attach event listeners once to prevent duplicates
  if (!eventListenersAttached) {
    socket.on("connect", () => {
      isInitializing = false;
    });

    socket.on("disconnect", (reason) => {
      isInitializing = false;
    });

    socket.on("connect_error", (error) => {
      isInitializing = false;
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
    });

    socket.on("reconnect_failed", () => {
      isInitializing = false;
      console.error("[Socket] Reconnect failed");
    });

    // Confirmation from the server that we joined the right tenant room
    socket.on("socket:authenticated", (data: { tenantId: string; userId: string }) => {
    });

    eventListenersAttached = true;
  }

  return socket;
}

/**
 * Update the auth token on the existing socket so reconnections use the latest JWT.
 */
export function updateSocketAuth(newToken: string): void {
  if (socket) {
    (socket as any).auth = { token: newToken };
    socket.io.opts.query = { token: newToken };
  }
}

export function disconnectSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    isInitializing = false;
    eventListenersAttached = false;
  }
}

